import { bindActionCreators } from "redux";

import store from "../store";
import { LOAD_MORE_LOG_DATA, NEW_LOG_DATA, DELETE_LOGS, SET_LOG_LEVEL } from "../ducks/logs";
import { CLEAR_ALL_STATISTIC_HIST } from "../ducks/nodeStatistics";
import { CLEAR_ALL_STATISTIC_LIVE, CLEAR_STATISTIC, SET_LIVE_STATISTIC } from "../ducks/nodeLiveStats";

import { actions as NODE_CHANNELS_ACTIONS } from "../ducks/nodeChannels";
import { actions as NODE_ACTIONS } from "../ducks/node";
import { actions as LOADING_ACTIONS } from "../ducks/loadingData";

import CloudChannelsServices from "./cloudChannels";

import { PlatformAPI } from "../lib/api";
import getMessageFromErrorCode from "../lib/utils/getMessageFromErrorCode";

const actions = bindActionCreators(
  {
    connectFingerprintToChannel:
      (data, { errorNotification }) =>
      async () => {
        try {
          await PlatformAPI.connectFingerprintToChannel(data, { errorNotification });
          await CloudChannelsServices.getOwnChannels({ errorNotification });
        } catch (error) {
          const errorMessage = getMessageFromErrorCode(error.status);
          errorNotification(errorMessage);
          throw new Error(`Error while connect fingerprint to cloud channel: ${error}`);
        }
      },
    connectFingerprintToPermission:
      (data, { errorNotification }) =>
      async () => {
        try {
          await PlatformAPI.connectFingerprintToPermission(data, { errorNotification });
          await CloudChannelsServices.getSharedChannels({ errorNotification });
        } catch (error) {
          const errorMessage = getMessageFromErrorCode(error.status);
          errorNotification(errorMessage);
          throw new Error(`Error while connect fingerprint to cloud permission: ${error}`);
        }
      },
    updateChannelData:
      ({ oldCloudIds, oldPermissionId }, { errorNotification }) =>
      async () => {
        try {
          if (oldCloudIds && oldCloudIds.length > 0) {
            await PlatformAPI.removeFingerprintFromChannel({ oldCloudIds }, { errorNotification });
          }
          if (oldPermissionId) {
            await PlatformAPI.removeFingerprintFromPermission({ oldPermissionId }, { errorNotification });
          }
          await CloudChannelsServices.getOwnChannels({ errorNotification });
          await CloudChannelsServices.getSharedChannels({ errorNotification });
        } catch (error) {
          const errorMessage = getMessageFromErrorCode({ status: error.status, code: error.errorCode });
          errorNotification(errorMessage);
        }
      },
    updateChannel: (data) => (dispatch) => {
      dispatch(NODE_CHANNELS_ACTIONS.CHANGE_NODES_CHANNELS_STATUS(data));
    },
    updateChannelsList: (data) => (dispatch) => {
      const connectedNodeCWID = store.getState().getIn(["node", "cwid"]);
      const dataCWID = data.from && data.from.split("/")[1];

      if (connectedNodeCWID === dataCWID) {
        dispatch(NODE_CHANNELS_ACTIONS.SET_NODES_CHANNELS(data?.list));
        dispatch(LOADING_ACTIONS.CLEAR_LOADING());
      }
    },
    setNetworkInterfaces: (data) => (dispatch) => {
      dispatch(NODE_ACTIONS.SET_NETWORKINTERFACES(data));
      dispatch(LOADING_ACTIONS.CLEAR_LOADING());
    },
    setNodeLicenseList: (data) => (dispatch) => {
      dispatch(NODE_ACTIONS.SET_NODE_LICENSE_LIST(data));
      dispatch(LOADING_ACTIONS.CLEAR_LOADING());
    },
    setStundAddress: (data) => (dispatch) => {
      dispatch(NODE_ACTIONS.SET_STUND_ADDRESS(data));
      dispatch(LOADING_ACTIONS.CLEAR_LOADING());
    },
    setFingerprint: (data) => (dispatch) => {
      dispatch(NODE_ACTIONS.SET_NODE_FINGERPRINT(data));
      dispatch(LOADING_ACTIONS.CLEAR_LOADING());
    },
    setShortFingerprint: (data) => (dispatch) => {
      dispatch(NODE_ACTIONS.SET_SHORT_FINGERPRINT(data));
      dispatch(LOADING_ACTIONS.CLEAR_LOADING());
    },
    updateChannelStatistic:
      ({
        c: channelId,
        lc: channel,
        lco: codecs,
        li: input,
        lmn: muxedNonUrl,
        lmu: muxedUrl,
        lnm: nonMuxed,
        t: timestamp,
        from,
      }) =>
      (dispatch) => {
        const channelTime = channel.map((channelElement) => ({ ...channelElement, time: timestamp }));
        const codecsTime = codecs.map((codecElement) => ({ ...codecElement, time: timestamp }));
        const inputTime = input.map((inputElement) => ({ ...inputElement, time: timestamp }));
        const muxedNonUrlTime = muxedNonUrl.map((muxedNonUrlElement) => ({ ...muxedNonUrlElement, time: timestamp }));
        const muxedUrlTime = muxedUrl.map((muxedUrlElement) => ({ ...muxedUrlElement, time: timestamp }));
        const nonMuxedTime = nonMuxed.map((nonMuxedElement) => ({ ...nonMuxedElement, time: timestamp }));
        dispatch(
          SET_LIVE_STATISTIC({
            data: {
              channel: channelTime,
              codecs: codecsTime,
              input: inputTime,
              muxedNonUrl: muxedNonUrlTime,
              muxedUrl: muxedUrlTime,
              nonMuxed: nonMuxedTime,
            },
            channelId,
            from,
          })
        );
      },
    clearStatistic:
      ({ channelId, from }) =>
      (dispatch) => {
        dispatch(CLEAR_STATISTIC({ channelId, from }));
      },
    loadMoreLogData:
      ({ logs, channelId }) =>
      (dispatch) => {
        dispatch(LOAD_MORE_LOG_DATA({ logs, channelId }));
      },
    deleteLogs: () => (dispatch) => {
      dispatch(DELETE_LOGS());
    },
    clearAllStatistic: () => (dispatch) => {
      dispatch(CLEAR_ALL_STATISTIC_HIST());
      dispatch(CLEAR_ALL_STATISTIC_LIVE());
    },
    newLogData: (data) => (dispatch) => {
      dispatch(NEW_LOG_DATA(data));
    },
    updateNodeCapabilities: (data) => (dispatch) => {
      dispatch(NODE_ACTIONS.SET_NODE_CAPABILITIES(data));
    },
    updateLogLevel: (data) => (dispatch) => {
      dispatch(SET_LOG_LEVEL({ ...data }));
    },
    deleteChannel:
      ({ cloudData }, { errorNotification }) =>
      async () => {
        try {
          if (cloudData.hasPermissionId) {
            await PlatformAPI.removeFingerprintFromPermission(
              { oldPermissionId: cloudData.hasPermissionId },
              { errorNotification }
            );
          }
          if (cloudData.hasCloudId) {
            await PlatformAPI.removeFingerprintFromChannel({ oldCloudId: cloudData.hasCloudId }, { errorNotification });
          }
        } catch (error) {
          const errorMessage = getMessageFromErrorCode("platformConnectionError");
          errorNotification(errorMessage);
        }
      },
  },
  store.dispatch
);

export default actions;
